"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
function LatestNewsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="146980" className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-[380px] w-full animate-pulse rounded-2xl bg-muted/10 border border-border/10" />)}
      </div>;
  }
  if (error || posts.length === 0) {
    return <div className="flex h-[380px] flex-col items-center justify-center rounded-2xl border border-dashed border-border/30 bg-muted/5 text-center p-8">
        <p className="text-muted-foreground font-sans mb-4">Unable to synchronize with the intelligence network.</p>
        <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10">Retry Connection</Button>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {posts.map((post, i) => <Link key={post.id} data-index={i} to={post.link || "#"} className="group relative block h-[380px] w-full overflow-hidden rounded-2xl border border-border/20 bg-card">
          <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633370/img/generic_ai_interface.png"} alt={post.title} className="absolute inset-0 h-full w-full object-cover grayscale transition-transform duration-700 group-hover:scale-105 group-hover:grayscale-0" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-primary/10" />
          
          <div className="absolute inset-0 flex flex-col justify-between p-6">
            <div className="flex items-center justify-between">
              <Badge variant="secondary" className="bg-primary/20 text-primary backdrop-blur-xl border-primary/30 font-sans">
                {post.categories?.[0]?.name || "Systems Architecture"}
              </Badge>
              <Badge variant="outline" className="bg-background/60 backdrop-blur-md text-foreground/90 border-border/50 font-sans">
                4 min read
              </Badge>
            </div>
            
            <div className="transform transition-transform duration-300 group-hover:-translate-y-2">
              <h3 className="text-xl font-default font-bold text-foreground mb-3 line-clamp-2 group-hover:text-primary transition-colors" dangerouslySetInnerHTML={{
            __html: post.title
          }} />
              <p className="text-sm font-sans text-muted-foreground line-clamp-2 mb-4" dangerouslySetInnerHTML={{
            __html: post.excerpt || "Analyzing the intersection of enterprise-grade AI execution and visionary product development."
          }} />
              <div className="flex items-center text-sm font-sans font-medium text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                Analyze Report <ArrowRight className="ml-2 h-4 w-4" />
              </div>
            </div>
          </div>
        </Link>)}
    </div>;
}
function BestOfConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="flex flex-col gap-4">
        {[...Array(5)].map((_, i) => <div key={i} data-index={i} className="flex items-center gap-4 p-3">
            <div className="h-10 w-10 animate-pulse rounded-md bg-muted/10" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-3/4 animate-pulse rounded bg-muted/10" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-muted/10" />
            </div>
          </div>)}
      </div>;
  }
  if (error || posts.length === 0) {
    return <div className="p-6 text-center text-sm font-sans text-muted-foreground border border-dashed border-border/30 rounded-xl">
        No priority systems currently indexed.
      </div>;
  }
  return <div className="flex flex-col gap-2">
      {posts.map((post, i) => <Link key={post.id} data-index={i} to={post.link || "#"} className="group flex items-center gap-5 rounded-xl border border-transparent p-4 transition-all hover:border-border/20 hover:bg-primary/5 hover:shadow-lg hover:shadow-primary/5">
          <span className="text-4xl font-default font-black text-secondary/30 group-hover:text-secondary transition-colors duration-300">
            {(i + 1).toString().padStart(2, '0')}
          </span>
          <div className="flex-1">
            <h4 className="text-base font-default font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors" dangerouslySetInnerHTML={{
          __html: post.title
        }} />
            <div className="mt-2 flex items-center gap-2 text-xs font-sans text-muted-foreground">
              <span className="uppercase tracking-widest font-medium text-secondary/90">{post.categories?.[0]?.name || "AI Compute"}</span>
              <span>•</span>
              <span>{post.date ? new Date(post.date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            }) : "Recently Synchronized"}</span>
            </div>
          </div>
        </Link>)}
    </div>;
}
export default function NeuralInsightsBlog() {
  const latestNewsParams = {
    per_page: 4,
    orderby: "date",
    order: "desc"
  };
  const latestNewsQuery = new WP_Query({
    post_type: "post",
    ...latestNewsParams
  });
  const bestOfParams = {
    per_page: 5,
    offset: 4,
    orderby: "date",
    order: "desc"
  };
  const bestOfQuery = new WP_Query({
    post_type: "post",
    ...bestOfParams
  });
  return <section id="intelligent-discourse" className="dark bg-background text-foreground py-24 px-4 md:px-8 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/10 blur-[140px] rounded-full pointer-events-none opacity-40" />
      
      <div className="mx-auto max-w-7xl relative z-10">
        <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/5 backdrop-blur-sm font-sans tracking-wider">
              Sovereign Intelligence
            </Badge>
            <h2 className="text-3xl md:text-5xl font-default font-bold tracking-tighter mb-4">
              Strategic <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Transmissions</span>
            </h2>
            <p className="text-lg font-sans text-muted-foreground">
              Deep-dive analysis into AI infrastructure, Salesforce ecosystem optimization, and the future of decentralized compute. Built for the architects of tomorrow.
            </p>
          </div>
          <Link to="#">
            <Button variant="outline" className="shrink-0 border-border/50 hover:bg-primary/10 hover:text-primary transition-all font-sans">
              Access Intelligence
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between border-b border-border/20 pb-4">
              <h3 className="text-xl font-default font-semibold tracking-tight flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                Infrastructure Logs
              </h3>
            </div>
            <PostsProvider wp_query={latestNewsQuery}>
              <LatestNewsConsumer />
            </PostsProvider>
          </div>

          {}
          <div className="lg:col-span-1 space-y-6">
            <div className="flex items-center justify-between border-b border-border/20 pb-4">
              <h3 className="text-xl font-default font-semibold tracking-tight">
                High-Priority Systems
              </h3>
            </div>
            <div className="rounded-2xl border border-border/20 bg-card/40 backdrop-blur-2xl p-2 shadow-2xl shadow-black/40">
              <PostsProvider wp_query={bestOfQuery}>
                <BestOfConsumer />
              </PostsProvider>
            </div>
            
            {}
            <div className="mt-8 rounded-2xl bg-gradient-to-br from-primary/30 to-secondary/30 p-[1px] shadow-lg shadow-primary/10">
              <div className="rounded-[15px] bg-background/95 backdrop-blur-3xl p-6 text-center">
                <h4 className="text-lg font-default font-bold mb-2">Sync with the Network</h4>
                <p className="text-sm font-sans text-muted-foreground mb-6">Receive high-fidelity reports on AI compute markets and enterprise systems integration.</p>
                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_25px_rgba(var(--color-primary),0.35)] font-sans font-semibold">
                  Establish Uplink
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}